import { useEffect } from 'react';
import { EventName } from '@/api/services/events';
import {
  CardStyleInterpolators,
  createStackNavigator,
  HeaderStyleInterpolators,
} from '@react-navigation/stack';
import { Aufgaben } from '@/screens/Aufgaben';
import { AufgabenExercise } from '@/screens/AufgabenExercise';
import { AufgabenExerciseText } from '@/screens/AufgabenExercise/text';
import { DigaCodes } from '@/screens/DigaCode';
import { EpaScreen } from '@/screens/EpaScreen';
import { Ergebnisse } from '@/screens/Ergebnisse';
import { Filler } from '@/screens/Filler';
import { Home } from '@/screens/HomePatient';
import { Instructions } from '@/screens/Instructions';
import { LicenseBooking } from '@/screens/LicenseBooking';
import { MarketingChannelQuestionnaire } from '@/screens/MarketingChannelQuestionnaire/';
import { PasswordChangeScreen } from '@/screens/PasswordChangeScreen';
import { PatientAccount } from '@/screens/PatientAccount';
import { PatientConsentForms } from '@/screens/PatientConsentForms';
import { Results } from '@/screens/Results';
import { Icon } from '@/components/Icon';
import colors, { appTheme } from '@/config/colors';
import { Patient } from '@/stores/store.types';
import { PatientDemoProvider } from '@/contexts/PatientDemoContext';
import { useNavigation } from '@react-navigation/native';
import { useTracking } from '@/hooks/useTracking';
import store, { useStoreState } from '@/stores/store';
import { DownloadProvider } from '@/contexts/DownloadAssetContext';
import { IS_IOS } from '@/utils/platform';

export type PatientStackParamList = {
  Anleitung: undefined;
  AufgabenExercise: undefined;
  AufgabenExerciseText: undefined;
  Aufgaben: {
    exerciseClass: string;
    patientId: string;
    setId: string;
    isNetworkError?: string;
    exerciseSettings: Record<string, unknown>;
    isSelf: boolean;
    selectedPatient?: Patient;
    activeSets?: unknown;
    preview: false;
  };
  DigaCodes: undefined;
  Home: undefined;
  Einwilligungen: undefined;
  EpaScreen: undefined;
  LicenseBooking: undefined;
  Login: undefined;
  MarketingChannelQuestionnaire: undefined;
  PasswordChangeScreen: undefined;
  PatientAccount: undefined;
  Privacy: undefined;
  ResultDetailScreen: undefined;
  ResultsScreen: undefined;
  Settings: undefined;
  SignUpController: undefined;
};

const Stack = createStackNavigator<PatientStackParamList>();

function PatientNavigationStack() {
  const needsMarketingChannel = useStoreState(
    (state) => state.signedInUser?.user?.marketingChannelSelection === null,
  );

  return (
    <Stack.Navigator
      initialRouteName={
        needsMarketingChannel ? 'MarketingChannelQuestionnaire' : 'Home'
      }
      screenOptions={{
        gestureEnabled: false,
        cardStyleInterpolator: CardStyleInterpolators.forNoAnimation,
        headerStyleInterpolator: HeaderStyleInterpolators.forNoAnimation,
        headerTintColor: colors.white,
        headerStyle: { backgroundColor: appTheme.primary },
        headerLeftContainerStyle: { paddingHorizontal: 8 },
        headerRightContainerStyle: { paddingHorizontal: 16 },
        headerBackTitle: 'Zurück',
        headerMode: 'float',
        headerTitleAlign: 'center',
        headerTitleStyle: {
          fontSize: 24,
        },
      }}
    >
      <Stack.Group
        screenOptions={{
          headerStyle: {
            height: 64,
            backgroundColor: appTheme.primary,
          },
          headerBackTitleStyle: {
            fontSize: 20,
          },
          headerTitleStyle: {
            fontSize: 24,
          },
          headerBackImage: (x) =>
            IS_IOS ? (
              <Icon name="chevronLeft" color={x.tintColor} size={38} />
            ) : (
              <Icon name="arrowLeft" color={x.tintColor} size={38} />
            ),
        }}
      >
        <Stack.Screen
          name="Privacy"
          component={Filler}
          options={{ headerShown: false, presentation: 'modal' }}
        />
        <Stack.Screen
          name="PatientAccount"
          component={PatientAccount}
          options={{
            headerTitle: 'Mein Konto',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="Home"
          component={Home}
          options={{
            headerTitle: 'Übungsset auswählen',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="AufgabenExercise"
          options={{
            title: '',
          }}
          component={AufgabenExercise}
        />
        <Stack.Screen
          name="AufgabenExerciseText"
          component={AufgabenExerciseText}
          options={{
            title: '',
          }}
        />
        <Stack.Screen
          name="Aufgaben"
          component={Aufgaben}
          options={{
            title: 'Aufgaben auswählen',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="Anleitung"
          component={Instructions}
          options={{
            title: 'Anleitung',
            headerTitleAllowFontScaling: false,
          }}
        />

        <Stack.Screen
          name="Einwilligungen"
          component={PatientConsentForms}
          options={{
            title: 'Einwilligungen',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="EpaScreen"
          component={EpaScreen}
          options={{
            title: 'Elektronische Patientenakte',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="MarketingChannelQuestionnaire"
          options={{
            title: '',
            headerTitleAllowFontScaling: false,
          }}
          component={MarketingChannelQuestionnaire}
        />
        <Stack.Screen
          name="PasswordChangeScreen"
          component={PasswordChangeScreen}
          options={{
            title: 'Passwort ändern',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="ResultsScreen"
          component={Results}
          options={{
            title: 'Ergebnisse',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="ResultDetailScreen"
          component={Ergebnisse}
          options={{
            title: 'Ergebnisse',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="DigaCodes"
          component={DigaCodes}
          options={{
            title: 'Rezeptcode einlösen',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="LicenseBooking"
          component={LicenseBooking}
          options={{
            title: 'Lizenz buchen',
            headerTitleAllowFontScaling: false,
          }}
        />
      </Stack.Group>
    </Stack.Navigator>
  );
}

export function PatientStack() {
  const { trackEvent } = useTracking();
  const user = store.getState().signedInUser?.user || null;
  const navigation = useNavigation();

  useEffect(() => {
    const unsubscribe = navigation.addListener('state', () => {
      const currentRoute = navigation.getState()?.routes.at(-1);
      trackEvent(user, EventName.SCREEN_VIEWED, {
        $screen_name: currentRoute?.name || 'Home',
      });
    });

    return unsubscribe;
  }, [navigation, trackEvent, user]);

  return (
    <DownloadProvider>
      <PatientDemoProvider>
        <PatientNavigationStack />
      </PatientDemoProvider>
    </DownloadProvider>
  );
}
